import { Action, createReducer, on } from '@ngrx/store';
import {
  BaseResponse,
  CounterResponse,
  EmptyResponse,
  PrinterGroupItem,
  PrinterGroupListResponse,
  PrinterItem,
  PrinterItemWithOtherMetrics,
  PrinterListResponse,
  PrinterReportAlertDescriptions,
  PrinterReportConsumable,
  PrinterReportCounterAnalytics,
  PrinterReportCounters,
  PrinterStatsItem,
  PrinterTrendAndForecast,
  ThresholdsResponse,
} from '../../../api/models';
import { ILoaderActionPayload, LoadingState } from '../models/loader.model';
import { printerActions } from './printer.action';

export interface PrinterState {
  loadMany: {
    state: ILoaderActionPayload;
    data?: PrinterListResponse;
  };
  loadManyModal: {
    state: ILoaderActionPayload;
    data?: PrinterListResponse;
  };
  loadOne: {
    state: ILoaderActionPayload;
    data?: PrinterItemWithOtherMetrics;
  };
  edit: {
    state: ILoaderActionPayload;
    data?: PrinterItem;
  };
  deleteOne: {
    state: ILoaderActionPayload;
    data?: EmptyResponse;
  };
  printerGroupLoadMany: { state: ILoaderActionPayload; data?: PrinterGroupListResponse };
  createGroup: { state: ILoaderActionPayload; data?: PrinterGroupItem };
  editGroup: { state: ILoaderActionPayload; data?: PrinterGroupItem };
  loadOneGroup: { state: ILoaderActionPayload; data?: PrinterGroupItem };
  deleteGroup: { state: ILoaderActionPayload };
  realTimeMetricsRequest: {
    state: ILoaderActionPayload;
    data?: BaseResponse;
  };
  realTimeMetricsRequestMultiple: {
    state: ILoaderActionPayload;
    data?: BaseResponse;
  };
  editPrintersManage: {
    state: ILoaderActionPayload;
    data?: CounterResponse;
  };
  statusCodeStats: {
    state: ILoaderActionPayload;
    data?: PrinterStatsItem;
  };
  loadDataReport: {
    state: ILoaderActionPayload;
    data?: PrinterReportConsumable;
  };
  loadCountersReport: {
    state: ILoaderActionPayload;
    data?: PrinterReportCounters;
  };
  loadAlertDescriptionsReport: {
    state: ILoaderActionPayload;
    data?: PrinterReportAlertDescriptions;
  };
  loadCountersAnalyticsReport: {
    state: ILoaderActionPayload;
    data?: PrinterReportCounterAnalytics;
  };
  loadStatusTrendReport: {
    state: ILoaderActionPayload;
    data?: PrinterReportCounters;
  };
  loadTrendForecast: {
    state: ILoaderActionPayload;
    data?: PrinterTrendAndForecast;
  };
  loadPrinterThresholds: {
    state: ILoaderActionPayload;
    data?: ThresholdsResponse;
  };
}

const initialState: PrinterState = {
  loadMany: { state: { value: LoadingState.NONE } },
  loadManyModal: { state: { value: LoadingState.NONE } },
  loadOne: { state: { value: LoadingState.NONE } },
  edit: { state: { value: LoadingState.NONE } },
  deleteOne: { state: { value: LoadingState.NONE } },
  printerGroupLoadMany: { state: { value: LoadingState.NONE } },
  createGroup: { state: { value: LoadingState.NONE } },
  loadOneGroup: { state: { value: LoadingState.NONE } },
  editGroup: { state: { value: LoadingState.NONE } },
  deleteGroup: { state: { value: LoadingState.NONE } },
  realTimeMetricsRequest: {
    state: {
      value: LoadingState.NONE,
    },
  },
  realTimeMetricsRequestMultiple: {
    state: {
      value: LoadingState.NONE,
    },
  },
  editPrintersManage: {
    state: {
      value: LoadingState.NONE,
    },
  },
  statusCodeStats: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadDataReport: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadCountersReport: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadAlertDescriptionsReport: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadCountersAnalyticsReport: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadStatusTrendReport: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadTrendForecast: {
    state: {
      value: LoadingState.NONE,
    },
  },
  loadPrinterThresholds: {
    state: {
      value: LoadingState.NONE,
    },
  },
};

const reducer = createReducer(
  initialState,
  on(printerActions.loadMany, (state, props) => ({
    ...state,
    ...(props.loadManyModal === 'ok'
      ? {
          loadManyModal: { state: { value: LoadingState.LOADING } },
        }
      : { loadMany: { state: { value: LoadingState.LOADING } } }),
  })),
  on(printerActions.loadManyDone, (state, props) => ({
    ...state,
    ...(props.loadManyModal === 'ok'
      ? { loadManyModal: { state: props.state, data: props.data } }
      : { loadMany: { state: props.state, data: props.data } }),
  })),
  on(printerActions.loadOne, (state) => ({
    ...state,
    loadOne: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadOneDone, (state, props) => ({
    ...state,
    loadOne: { state: props.state, data: props.data },
  })),
  on(printerActions.edit, (state) => ({
    ...state,
    edit: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.editDone, (state, props) => ({
    ...state,
    edit: { state: props.state, data: props.data },
  })),
  on(printerActions.editSetState, (stete, props) => ({
    ...stete,
    edit: { state: props.state },
  })),
  on(printerActions.loadManyGroup, (state) => ({
    ...state,
    printerGroupLoadMany: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadManyGroupDone, (state, props) => ({
    ...state,
    printerGroupLoadMany: { state: props.state, data: props.data },
  })),
  on(printerActions.loadOneGroup, (state) => ({
    ...state,
    loadOneGroup: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadOneGroupDone, (state, props) => ({
    ...state,
    loadOneGroup: { state: props.state, data: props.data },
  })),
  on(printerActions.createGroup, (state) => ({
    ...state,
    createGroup: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.createGroupDone, (state, props) => ({
    ...state,
    createGroup: { state: props.state, data: props.data },
  })),
  on(printerActions.createGroupSetState, (state, props) => ({
    ...state,
    createGroup: { state: props.state },
  })),
  on(printerActions.editGroup, (state) => ({
    ...state,
    editGroup: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.editGroupDone, (state, props) => ({
    ...state,
    editGroup: { state: props.state, data: props.data },
    printerGroupLoadMany: {
      ...state.printerGroupLoadMany,
      data: {
        ...state.printerGroupLoadMany.data,
        list:
          state.printerGroupLoadMany.data?.list?.map((item) =>
            item.id === props.data?.id ? { ...item, ...props.data } : item,
          ) ?? [],
        count: state.printerGroupLoadMany.data?.count ?? 0,
      },
    },
  })),
  on(printerActions.editGroupSetState, (state, props) => ({
    ...state,
    editGroup: { state: props.state },
  })),
  on(printerActions.deleteGroup, (state) => ({
    ...state,
    deleteGroup: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.deleteGroupDone, (state, props) => ({
    ...state,
    deleteGroup: { state: props.state },
  })),
  on(printerActions.deleteGroupSetState, (state, props) => ({
    ...state,
    deleteGroup: { state: props.state },
  })),
  //real-time-metrics-request
  on(printerActions.realTimeMetricsRequest, (state) => ({
    ...state,
    realTimeMetricsRequest: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.realTimeMetricsRequestDone, (state, props) => ({
    ...state,
    realTimeMetricsRequest: { state: props.state, data: props.data },
  })),
  on(printerActions.realTimeMetricsRequestSetState, (state, props) => ({
    ...state,
    realTimeMetricsRequest: { state: props.state },
  })),
  //real-time-metrics-request-multiple
  on(printerActions.realTimeMetricsRequestMultiple, (state) => ({
    ...state,
    realTimeMetricsRequestMultiple: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.realTimeMetricsRequestMultipleDone, (state, props) => ({
    ...state,
    realTimeMetricsRequestMultiple: { state: props.state, data: props.data },
  })),
  on(printerActions.realTimeMetricsRequestMultipleSetState, (state, props) => ({
    ...state,
    realTimeMetricsRequestMultiple: { state: props.state },
  })),
  //edit-printers-manage
  on(printerActions.editPrintersManage, (state) => ({
    ...state,
    editPrintersManage: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.editPrintersManageDone, (state, props) => ({
    ...state,
    editPrintersManage: { state: props.state, data: props.data },
  })),
  on(printerActions.editPrintersManageSetState, (state, props) => ({
    ...state,
    editPrintersManage: { state: props.state },
  })),
  //status code stats
  on(printerActions.statusCodeStats, (state) => ({
    ...state,
    statusCodeStats: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.statusCodeStatsDone, (state, props) => ({
    ...state,
    statusCodeStats: { state: props.state, data: props.data },
  })),
  on(printerActions.statusCodeStatsSetState, (state, props) => ({
    ...state,
    statusCodeStats: { state: props.state },
  })),
  on(printerActions.loadDataReport, (state) => ({
    ...state,
    loadDataReport: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadDataReportDone, (state, props) => ({
    ...state,
    loadDataReport: { state: props.state, data: props.data },
  })),
  on(printerActions.loadCountersReport, (state) => ({
    ...state,
    loadCountersReport: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadCountersReportDone, (state, props) => ({
    ...state,
    loadCountersReport: { state: props.state, data: props.data },
  })),
  on(printerActions.loadAlertDescriptionsReport, (state) => ({
    ...state,
    loadAlertDescriptionsReport: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadAlertDescriptionsReportDone, (state, props) => ({
    ...state,
    loadAlertDescriptionsReport: { state: props.state, data: props.data },
  })),
  on(printerActions.loadCountersAnalyticsReport, (state) => ({
    ...state,
    loadCountersAnalyticsReport: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadCountersAnalyticsReportDone, (state, props) => ({
    ...state,
    loadCountersAnalyticsReport: { state: props.state, data: props.data },
  })),
  on(printerActions.loadStatusTrendReport, (state) => ({
    ...state,
    loadStatusTrendReport: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadStatusTrendReportDone, (state, props) => ({
    ...state,
    loadStatusTrendReport: { state: props.state, data: props.data },
  })),
  on(printerActions.loadTrendForecast, (state) => ({
    ...state,
    loadTrendForecast: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadTrendForecastDone, (state, props) => ({
    ...state,
    loadTrendForecast: { state: props.state, data: props.data },
  })),
  on(printerActions.loadPrinterThresholds, (state) => ({
    ...state,
    loadPrinterThresholds: { state: { value: LoadingState.LOADING } },
  })),
  on(printerActions.loadPrinterThresholdsDone, (state, props) => ({
    ...state,
    loadPrinterThresholds: { state: props.state, data: props.data },
  })),
);

export function printerReducer(state: PrinterState | undefined, action: Action) {
  return reducer(state, action);
}
